// §27 — Reminders: shared task lists, and the groups you share them with.
//
// **Nothing here decides who may do what.** The server resolves a role per request (`roleFor`), and
// this file renders it: `list.role` is what disables a control, and a disabled control is only ever
// a *hint* — the route refuses it too, and would still refuse it if this file were wrong. That is
// the difference between a permission and a stylesheet.
//
// The doorbell (§26.6) is why a shared list is worth having: a `reminder` frame means somebody else
// changed something, and the app answers by re-`GET`ting exactly what a cold load would. One code
// path produces a list, so "works when I open it, stale when they edit it" is not a state that can
// exist.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  canEditItems,
  canManageShares,
  dueBucket,
  formatDue,
  parseDue,
  sortReminders,
  type Connection,
  type Reminder,
  type ReminderList,
  type ReminderShare,
  type RemindersShellAccess,
  type UserGroup,
} from '@hangar/shared';
import { COPY } from '@/copy';
import './reminders.css';

export interface RemindersProps {
  access?: RemindersShellAccess;
}

const DUE_LABEL: Readonly<Record<string, string>> = {
  overdue: COPY.reminders.overdue,
  today: COPY.reminders.dueToday,
  soon: COPY.reminders.dueSoon,
  later: COPY.reminders.dueLater,
};

export function Reminders({ access }: RemindersProps) {
  const [lists, setLists] = useState<readonly ReminderList[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [items, setItems] = useState<readonly Reminder[]>([]);
  const [shares, setShares] = useState<readonly ReminderShare[]>([]);
  const [groups, setGroups] = useState<readonly UserGroup[]>([]);
  const [connections, setConnections] = useState<readonly Connection[]>([]);
  const [panel, setPanel] = useState<'none' | 'share' | 'groups'>('none');
  const [draft, setDraft] = useState('');
  const [due, setDue] = useState('');
  const [newList, setNewList] = useState('');
  const [newGroup, setNewGroup] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const now = Date.now();

  const list = useMemo(() => lists.find((one) => one.id === selected) ?? null, [lists, selected]);
  const role = list?.role ?? 'viewer';
  const mayEdit = list !== null && canEditItems(role);

  const loadLists = useCallback(async () => {
    if (access === undefined) return;
    const rows = await access.lists().catch(() => [] as ReminderList[]);
    setLists(rows);
    // Never leave the pane pointing at a list that has gone — an un-shared list disappears from
    // under you, which is the one thing a shared document does that a private one cannot.
    setSelected((current) =>
      current !== null && rows.some((one) => one.id === current) ? current : (rows[0]?.id ?? null),
    );
  }, [access]);

  const loadItems = useCallback(
    async (listId: string | null) => {
      if (access === undefined || listId === null) {
        setItems([]);
        return;
      }
      setItems(await access.items(listId).catch(() => [] as Reminder[]));
    },
    [access],
  );

  useEffect(() => {
    void loadLists();
  }, [loadLists]);

  useEffect(() => {
    void loadItems(selected);
    setNotice(null);
  }, [loadItems, selected]);

  // §26.6 — somebody else changed a list. Both halves re-read: the sidebar carries the open counts
  // and the shared marks, so refreshing only the open list would leave those stale.
  useEffect(() => {
    if (access === undefined) return;
    return access.subscribe((event) => {
      if (event.kind !== 'reminder') return;
      void loadLists();
      void loadItems(selected);
    });
  }, [access, loadLists, loadItems, selected]);

  const guarded = useCallback(async (work: () => Promise<unknown>) => {
    try {
      await work();
      setNotice(null);
    } catch (error: unknown) {
      const status = (error as { status?: number } | null)?.status;
      // The two refusals a person actually runs into, said as rules rather than as errors.
      setNotice(status === 403 ? COPY.reminders.readOnly : null);
    }
  }, []);

  const addItem = useCallback(async () => {
    if (access === undefined || list === null) return;
    const title = draft.trim();
    if (title.length === 0) return;
    await guarded(async () => {
      await access.addItem(list.id, { title, dueAt: parseDue(due) });
      setDraft('');
      setDue('');
      await loadItems(list.id);
      await loadLists();
    });
  }, [access, list, draft, due, guarded, loadItems, loadLists]);

  const toggle = useCallback(
    async (item: Reminder) => {
      if (access === undefined || list === null) return;
      await guarded(async () => {
        await access.updateItem(item.id, { completed: item.completedAt === null });
        await loadItems(list.id);
        await loadLists();
      });
    },
    [access, list, guarded, loadItems, loadLists],
  );

  const removeItem = useCallback(
    async (item: Reminder) => {
      if (access === undefined || list === null) return;
      if (!(await access.confirm(COPY.reminders.confirmDeleteItem))) return;
      await guarded(async () => {
        await access.deleteItem(item.id);
        await loadItems(list.id);
        await loadLists();
      });
    },
    [access, list, guarded, loadItems, loadLists],
  );

  const openShare = useCallback(async () => {
    if (access === undefined || list === null) return;
    setPanel('share');
    const [current, roster, mine] = await Promise.all([
      access.shares(list.id).catch(() => [] as ReminderShare[]),
      access.connections().catch(() => [] as Connection[]),
      access.groups().catch(() => [] as UserGroup[]),
    ]);
    setShares(current);
    setConnections(roster.filter((one) => one.state === 'accepted'));
    setGroups(mine);
  }, [access, list]);

  const openGroups = useCallback(async () => {
    if (access === undefined) return;
    setPanel('groups');
    const [mine, roster] = await Promise.all([
      access.groups().catch(() => [] as UserGroup[]),
      access.connections().catch(() => [] as Connection[]),
    ]);
    setGroups(mine);
    setConnections(roster.filter((one) => one.state === 'accepted'));
  }, [access]);

  const shown = useMemo(() => sortReminders(items), [items]);
  const sharedNames = useMemo(() => new Set(shares.map((share) => `${share.kind}:${share.id}`)), [shares]);

  return (
    <div className="rem" data-testid="reminders">
      <aside className="rem-side">
        <div className="rem-side-head">
          <h2 className="rem-side-title">{COPY.reminders.myLists}</h2>
          <button type="button" data-testid="rem-groups-open" onClick={() => void openGroups()}>
            {COPY.reminders.groups}
          </button>
        </div>

        {lists.length === 0 ? (
          <p className="rem-empty" data-testid="rem-no-lists">
            {COPY.reminders.noLists}
          </p>
        ) : (
          <ul className="rem-lists" data-testid="rem-lists">
            {lists.map((one) => (
              <li key={one.id}>
                <button
                  type="button"
                  className="rem-list"
                  data-testid={`rem-list-${one.id}`}
                  aria-current={one.id === selected}
                  onClick={() => setSelected(one.id)}
                >
                  <span className="rem-dot" style={{ background: `hsl(${one.hue} 70% 55%)` }} />
                  <span className="rem-list-name">{one.name}</span>
                  {/* A list somebody else owns says whose it is — with two people's lists in one
                      sidebar, the name alone stops being enough to tell them apart. */}
                  {one.role === 'owner' ? null : (
                    <span className="rem-owner" data-testid={`rem-owner-${one.id}`}>
                      {COPY.reminders.ownedBy(one.ownerName)}
                    </span>
                  )}
                  {one.shared && one.role === 'owner' ? (
                    <span className="rem-shared" data-testid={`rem-shared-${one.id}`}>
                      {COPY.reminders.sharedMark}
                    </span>
                  ) : null}
                  {one.openCount > 0 ? (
                    <span className="rem-count" data-testid={`rem-count-${one.id}`}>
                      {COPY.reminders.openCount(one.openCount)}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}

        <form
          className="rem-new"
          onSubmit={(event) => {
            event.preventDefault();
            const name = newList.trim();
            if (access === undefined || name.length === 0) return;
            void access.createList(name).then(() => {
              setNewList('');
              return loadLists();
            });
          }}
        >
          <input
            data-testid="rem-new-list"
            value={newList}
            placeholder={COPY.reminders.listNamePlaceholder}
            onChange={(event) => setNewList(event.currentTarget.value)}
          />
          <button type="submit" data-testid="rem-new-list-add" disabled={newList.trim().length === 0}>
            {COPY.reminders.newList}
          </button>
        </form>
      </aside>

      <section className="rem-main">
        {list === null ? (
          <p className="rem-empty" data-testid="rem-none-selected">
            {COPY.reminders.noneSelected}
          </p>
        ) : (
          <>
            <header className="rem-head">
              <h1 className="rem-title" data-testid="rem-title">
                {list.name}
              </h1>
              {canManageShares(role) ? (
                <>
                  <button type="button" data-testid="rem-share-open" onClick={() => void openShare()}>
                    {COPY.reminders.share}
                  </button>
                  <button
                    type="button"
                    data-testid="rem-delete-list"
                    onClick={() => {
                      if (access === undefined) return;
                      void access.confirm(COPY.reminders.confirmDeleteList(list.name)).then((yes) => {
                        if (!yes) return;
                        return access.deleteList(list.id).then(loadLists);
                      });
                    }}
                  >
                    {COPY.reminders.deleteList}
                  </button>
                </>
              ) : null}
            </header>

            {notice === null ? null : (
              <p className="rem-notice" role="status" data-testid="rem-notice">
                {notice}
              </p>
            )}

            {mayEdit ? (
              <form
                className="rem-add"
                onSubmit={(event) => {
                  event.preventDefault();
                  void addItem();
                }}
              >
                <input
                  data-testid="rem-draft"
                  value={draft}
                  placeholder={COPY.reminders.addPlaceholder}
                  onChange={(event) => setDraft(event.currentTarget.value)}
                />
                <input
                  data-testid="rem-due"
                  type="datetime-local"
                  aria-label={COPY.reminders.duePlaceholder}
                  value={due}
                  onChange={(event) => setDue(event.currentTarget.value)}
                />
                <button type="submit" data-testid="rem-add" disabled={draft.trim().length === 0}>
                  {COPY.reminders.add}
                </button>
              </form>
            ) : (
              <p className="rem-notice" data-testid="rem-read-only">
                {COPY.reminders.readOnly}
              </p>
            )}

            {shown.length === 0 ? (
              <p className="rem-empty" data-testid="rem-no-items">
                {COPY.reminders.noItems}
              </p>
            ) : (
              <ul className="rem-items" data-testid="rem-items">
                {shown.map((item) => {
                  const bucket = dueBucket(item.dueAt, now);
                  return (
                    <li
                      className="rem-item"
                      key={item.id}
                      data-testid={`rem-item-${item.id}`}
                      data-done={item.completedAt !== null}
                    >
                      <input
                        type="checkbox"
                        data-testid={`rem-check-${item.id}`}
                        aria-label={item.title}
                        checked={item.completedAt !== null}
                        disabled={!mayEdit}
                        onChange={() => void toggle(item)}
                      />
                      <span className="rem-item-body">
                        <span className="rem-item-title">{item.title}</span>
                        <span className="rem-item-meta">
                          {item.dueAt === null ? null : (
                            <span
                              className="rem-due"
                              data-bucket={bucket}
                              data-testid={`rem-due-${item.id}`}
                            >
                              {formatDue(item.dueAt, now)}
                              {bucket === 'overdue' ? ` · ${DUE_LABEL['overdue'] ?? ''}` : ''}
                            </span>
                          )}
                          {/* Who ticked it — the one fact a shared list adds that a private one
                              cannot, and the answer to "has somebody already done this". */}
                          {item.completedBy === null ? null : (
                            <span className="rem-by" data-testid={`rem-by-${item.id}`}>
                              {item.completedBy === access?.me()
                                ? COPY.reminders.completedByYou
                                : COPY.reminders.completedBy(item.completedByName ?? '')}
                            </span>
                          )}
                        </span>
                      </span>
                      {mayEdit ? (
                        <button
                          type="button"
                          className="rem-item-remove"
                          data-testid={`rem-remove-${item.id}`}
                          onClick={() => void removeItem(item)}
                        >
                          {COPY.reminders.remove}
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </section>

      {panel === 'share' && list !== null ? (
        <aside className="rem-panel" data-testid="rem-share-panel">
          <header className="rem-panel-head">
            <h2>{COPY.reminders.shareTitle(list.name)}</h2>
            <button type="button" data-testid="rem-panel-close" onClick={() => setPanel('none')}>
              {COPY.reminders.close}
            </button>
          </header>

          {shares.length === 0 ? (
            <p className="rem-empty" data-testid="rem-no-shares">
              {COPY.reminders.sharedWithNobody}
            </p>
          ) : (
            <ul className="rem-shares" data-testid="rem-shares">
              {shares.map((share) => (
                <li key={`${share.kind}:${share.id}`} data-testid={`rem-share-${share.id}`}>
                  <span className="rem-share-name">{share.name}</span>
                  <span className="rem-share-kind">{share.kind === 'group' ? COPY.reminders.groups : ''}</span>
                  <span className="rem-share-right">
                    {share.canEdit ? COPY.reminders.canEdit : COPY.reminders.canView}
                  </span>
                  <button
                    type="button"
                    data-testid={`rem-unshare-${share.id}`}
                    onClick={() => {
                      if (access === undefined) return;
                      void access
                        .unshare(list.id, share.kind, share.id)
                        .then(() => access.shares(list.id))
                        .then(setShares)
                        .then(loadLists);
                    }}
                  >
                    {COPY.reminders.unshare}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <h3 className="rem-panel-sub">{COPY.reminders.addPerson}</h3>
          {connections.length === 0 ? (
            <p className="rem-empty" data-testid="rem-no-connections">
              {COPY.reminders.noConnections}
            </p>
          ) : (
            <ul className="rem-candidates">
              {connections
                .filter((one) => !sharedNames.has(`user:${one.peer}`))
                .map((one) => (
                  <li key={one.peer}>
                    <span>{one.displayName}</span>
                    <button
                      type="button"
                      data-testid={`rem-share-add-${one.peer}`}
                      onClick={() => {
                        if (access === undefined) return;
                        void access.share(list.id, 'user', one.peer, true).then((next) => {
                          setShares(next);
                          return loadLists();
                        });
                      }}
                    >
                      {COPY.reminders.share}
                    </button>
                  </li>
                ))}
            </ul>
          )}

          <h3 className="rem-panel-sub">{COPY.reminders.addGroup}</h3>
          <ul className="rem-candidates">
            {groups
              .filter((one) => !sharedNames.has(`group:${one.id}`))
              .map((one) => (
                <li key={one.id}>
                  <span>
                    {one.name} · {COPY.reminders.members(one.members.length)}
                  </span>
                  <button
                    type="button"
                    data-testid={`rem-share-group-${one.id}`}
                    onClick={() => {
                      if (access === undefined) return;
                      void access.share(list.id, 'group', one.id, true).then((next) => {
                        setShares(next);
                        return loadLists();
                      });
                    }}
                  >
                    {COPY.reminders.share}
                  </button>
                </li>
              ))}
          </ul>
        </aside>
      ) : null}

      {panel === 'groups' ? (
        <aside className="rem-panel" data-testid="rem-groups-panel">
          <header className="rem-panel-head">
            <h2>{COPY.reminders.groupsTitle}</h2>
            <button type="button" data-testid="rem-panel-close" onClick={() => setPanel('none')}>
              {COPY.reminders.close}
            </button>
          </header>

          <form
            className="rem-new"
            onSubmit={(event) => {
              event.preventDefault();
              const name = newGroup.trim();
              if (access === undefined || name.length === 0) return;
              void access.createGroup(name).then(() => {
                setNewGroup('');
                return access.groups().then(setGroups);
              });
            }}
          >
            <input
              data-testid="rem-new-group"
              value={newGroup}
              placeholder={COPY.reminders.groupNamePlaceholder}
              onChange={(event) => setNewGroup(event.currentTarget.value)}
            />
            <button type="submit" data-testid="rem-new-group-add" disabled={newGroup.trim().length === 0}>
              {COPY.reminders.newGroup}
            </button>
          </form>

          {groups.length === 0 ? (
            <p className="rem-empty" data-testid="rem-no-groups">
              {COPY.reminders.noGroups}
            </p>
          ) : (
            <ul className="rem-groups" data-testid="rem-groups">
              {groups.map((group) => (
                <li key={group.id} className="rem-group" data-testid={`rem-group-${group.id}`}>
                  <div className="rem-group-head">
                    <span className="rem-group-name">{group.name}</span>
                    <span className="rem-group-count">{COPY.reminders.members(group.members.length)}</span>
                    <button
                      type="button"
                      data-testid={`rem-group-delete-${group.id}`}
                      onClick={() => {
                        if (access === undefined) return;
                        void access
                          .confirm(COPY.reminders.confirmDeleteGroup(group.name))
                          .then((yes) => {
                            if (!yes) return undefined;
                            return access
                              .deleteGroup(group.id)
                              .then(() => access.groups().then(setGroups))
                              .then(loadLists);
                          });
                      }}
                    >
                      {COPY.reminders.deleteGroup}
                    </button>
                  </div>
                  <ul className="rem-members">
                    {group.members.map((member) => (
                      <li key={member.ref} data-testid={`rem-member-${group.id}-${member.ref}`}>
                        <span>{member.displayName}</span>
                        <button
                          type="button"
                          data-testid={`rem-member-remove-${group.id}-${member.ref}`}
                          onClick={() => {
                            if (access === undefined) return;
                            void access
                              .removeMember(group.id, member.ref)
                              .then(() => access.groups().then(setGroups))
                              .then(loadLists);
                          }}
                        >
                          {COPY.reminders.unshare}
                        </button>
                      </li>
                    ))}
                    {connections
                      .filter((one) => !group.members.some((member) => member.ref === one.peer))
                      .map((one) => (
                        <li key={`add-${one.peer}`} className="rem-member-add">
                          <span>{one.displayName}</span>
                          <button
                            type="button"
                            data-testid={`rem-member-add-${group.id}-${one.peer}`}
                            onClick={() => {
                              if (access === undefined) return;
                              void access
                                .addMember(group.id, one.peer)
                                .then(() => access.groups().then(setGroups))
                                .then(loadLists);
                            }}
                          >
                            {COPY.reminders.addMember}
                          </button>
                        </li>
                      ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </aside>
      ) : null}
    </div>
  );
}

export default Reminders;
